import React from "react";
import {Col, Row} from "./styles/styleds";
import PlacesAutoCompleteInput from "../common/PlacesAutoCompleteInput";
import {Button, Divider, Icon, Tooltip} from "@blueprintjs/core";
import styled from "@emotion/styled";
import {COLORS, EmptyValueStrings} from "../../constants/constants";
import {HomeIcon, LocationIcon, MixerIcon} from "../common/icons";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faWarehouse} from "@fortawesome/pro-solid-svg-icons";
import {AddressType} from "./types/types";

interface AddressInputProps {
  onAddressChange: (value: string) => void;
  onAddressTypeChange: (addressType: AddressType | null) => void;
  address: string | null;
  addressType: AddressType | null;
  showValue?: boolean;
  isValid?: boolean;
  dataTestIdPrefix?: string;
  allowDeselect?: boolean;
  showStartLocationButton?: boolean;
  showWarehouseButton?: boolean;
  warehouseAddress?: string;
  showGpsButton?: boolean;
  gpsDisabled?: boolean;
  gpsTooltip?: string;
}

const getLabelForAddressType = (addressType: AddressType | null): string => {
  switch (addressType) {
    case AddressType.HOME:
      return "Home";
    case AddressType.START_LOCATION:
      return "Start Location";
    case AddressType.WAREHOUSE:
      return "Warehouse";
    case AddressType.GPS:
      return "Current Location";
    default:
      return "";
  }
};

const AddressInput = ({
  onAddressChange,
  onAddressTypeChange,
  address,
  addressType,
  showValue = true,
  isValid = true,
  dataTestIdPrefix,
  allowDeselect = false,
  showStartLocationButton = false,
  showWarehouseButton = false,
  warehouseAddress,
  showGpsButton = false,
  gpsDisabled = false,
  gpsTooltip
}: AddressInputProps) => {
  const handleOnAddressTypeChange = (newAddressType: AddressType) => {
    if (allowDeselect && newAddressType === addressType) {
      onAddressTypeChange(null);
      return;
    }
    if (newAddressType === addressType) {
      return;
    }
    onAddressTypeChange(newAddressType);
  };

  const isDeselected = addressType === null;
  const isReadOnly =
    addressType === AddressType.HOME ||
    addressType === AddressType.START_LOCATION ||
    addressType === AddressType.WAREHOUSE ||
    addressType === AddressType.GPS;
  const label = getLabelForAddressType(addressType);
  const displayAddress = addressType === AddressType.WAREHOUSE ? warehouseAddress ?? address : address;

  return (
    <Container style={isDeselected ? {opacity: 0.5} : undefined}>
      <Col>
        <Row
          style={
            isValid
              ? {}
              : {
                  borderRadius: "8px 0 0 8px",
                  border: `1px solid ${COLORS.COLOR_BORDER_ERROR}`,
                  height: "32px"
                }
          }
          gap={4}
        >
          {isDeselected ? (
            <Col style={{marginLeft: "8px", color: COLORS.TEXT_GRAY}}>{EmptyValueStrings.notProvided}</Col>
          ) : (
            <>
              {label && (
                <Col style={{marginLeft: "8px", color: COLORS.TEXT_GRAY, whiteSpace: "nowrap"}} span={"fit"}>
                  {label}
                </Col>
              )}
              {isReadOnly && !displayAddress && showValue ? (
                <Col style={{marginLeft: "8px", color: COLORS.TEXT_GRAY}}>
                  {addressType === AddressType.GPS ? "Unable to geocode or unreliable" : EmptyValueStrings.notProvided}
                </Col>
              ) : (
                <PlacesAutoCompleteInput
                  disabled={isReadOnly}
                  value={displayAddress ?? ""}
                  onChange={onAddressChange}
                  dataTestIdPrefix={dataTestIdPrefix}
                />
              )}
            </>
          )}
        </Row>
      </Col>
      <Divider color={COLORS.COLOR_BORDER} style={{margin: 0, height: "32px"}} />
      <Col span={"fit"}>
        <Row gap={4} style={{padding: "0 4px"}}>
          <Tooltip content="Home" placement="top">
            <AddressButton
              data-testid={`${dataTestIdPrefix}-home-address-button`}
              onClick={handleOnAddressTypeChange.bind(null, AddressType.HOME)}
              active={addressType === AddressType.HOME}
              icon={<Icon icon={<HomeIcon fill={addressType === AddressType.HOME ? COLORS.WHITE : undefined} />} />}
            />
          </Tooltip>
          {showWarehouseButton && (
            <Tooltip content="Warehouse" placement="top">
              <AddressButton
                data-testid={`${dataTestIdPrefix}-warehouse-address-button`}
                onClick={handleOnAddressTypeChange.bind(null, AddressType.WAREHOUSE)}
                active={addressType === AddressType.WAREHOUSE}
                icon={
                  <FontAwesomeIcon
                    icon={faWarehouse}
                    style={{
                      fontSize: "14px",
                      color: addressType === AddressType.WAREHOUSE ? COLORS.WHITE : "#6B7280"
                    }}
                  />
                }
              />
            </Tooltip>
          )}
          {showStartLocationButton && (
            <Tooltip content="Start Location" placement="top">
              <AddressButton
                data-testid={`${dataTestIdPrefix}-start-location-button`}
                onClick={handleOnAddressTypeChange.bind(null, AddressType.START_LOCATION)}
                active={addressType === AddressType.START_LOCATION}
                icon={
                  <Icon
                    icon={<LocationIcon fill={addressType === AddressType.START_LOCATION ? COLORS.WHITE : undefined} />}
                  />
                }
              />
            </Tooltip>
          )}
          {showGpsButton && (
            <Tooltip content={gpsTooltip || "Current Location"} placement="top">
              <AddressButton
                data-testid={`${dataTestIdPrefix}-gps-button`}
                onClick={gpsDisabled ? undefined : handleOnAddressTypeChange.bind(null, AddressType.GPS)}
                active={addressType === AddressType.GPS}
                disabled={gpsDisabled}
                icon={
                  <Icon
                    icon="locate"
                    size={16}
                    color={addressType === AddressType.GPS ? COLORS.WHITE : gpsDisabled ? "#bbb" : "#6B7280"}
                  />
                }
              />
            </Tooltip>
          )}
          <Tooltip content="Customize" placement="top">
            <AddressButton
              data-testid={`${dataTestIdPrefix}-custom-address-button`}
              onClick={handleOnAddressTypeChange.bind(null, AddressType.CUSTOM)}
              active={addressType === AddressType.CUSTOM}
              icon={<Icon icon={<MixerIcon fill={addressType === AddressType.CUSTOM ? COLORS.WHITE : undefined} />} />}
            />
          </Tooltip>
        </Row>
      </Col>
    </Container>
  );
};

export default AddressInput;

const AddressButton = styled(Button)`
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 6px;
  border: none !important;
  box-shadow: none !important;
  height: 26px;
  width: 26px;
  min-height: 0;
  min-width: 0;
  background-color: ${({active}) => (active ? COLORS.BLUE : "transparent")} !important;

  &:hover {
    background-color: ${({active}) => (active ? COLORS.BLUE : COLORS.BG_GRAY)} !important;
  }
`;

const Container = styled(Row)`
  border-radius: 8px;
  border: 1px solid ${COLORS.COLOR_BORDER};
  background-color: ${COLORS.WHITE};
  align-items: center;
  justify-content: flex-end;
  gap: 0;
  height: 32px;

  .bp5-popover-target {
    margin: 0 !important;
    width: 100% !important;

    .location-search-input {
      border-radius: 8px;
      border: none !important;
      background: none;
    }
  }
`;
